import type { GraphQLResult } from 'aws-amplify/api'
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  Action,
  Product,
  ProductRestoration,
  ProductStatus,
  ProductStatusesQueryData,
  ProductStatusProps,
  Progress,
  Quantity,
  Status,
} from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  closeAndCreateProductStatusMutation,
  createProductBatchAndStatusMutation,
  createProductWithStatusMutation,
  updateProductStatusMutation,
} from '@/services/graphql/mutations'
import type {
  ProductStatusUpdatePrecondition,
  ProductStatusUpdateReceipt,
} from '@/services/graphql/product'
import {
  matchesProductStatusesDestination,
  replaceProductStatusInQueryData,
  toProductStatusesQueryData,
} from '@/services/product/productStatuses.cache'
import {
  hasTemporaryProductSlugIdentity,
  isTemporaryProductIdentity,
} from '@/domain/product/productSlug'
import { usePrepareProductCreateAuthorizerScope } from '@/services/product/productCreateAuthorizerScope'
import { cleanInput } from '@/utils'
import {
  buildCreateProductWithStatusInput,
  type ProductWithStatusCreation,
} from '@/services/product/createProductWithStatus'

export { CREATE_PRODUCT_WITH_STATUS_FIELDS } from '@/services/product/createProductWithStatus'

const client = getAmplifyClient()
type ProductStatusInput = Partial<Product> & {
  id: string
  productID: string
  proofPictures?: string[]
  updatedBy: string | undefined
}

type UpsertProductOptions = {
  /**
   * When false, skip per-mutation query invalidations.
   * Useful for bulk operations where we invalidate once at the end.
   */
  invalidate?: boolean
  /** Restore caller-owned UI state when the mutation is rejected. */
  onError?: () => void
  /** Advance caller-owned confirmed state when the mutation succeeds. */
  onSuccess?: (receipt?: ProductStatusUpdateReceipt) => void
  /** Release caller-owned pending state after either outcome. */
  onSettled?: () => void
}

type BaseProductVariables = {
  input: Partial<Product> & {
    id: string
    productID: string
    progress: Progress
    proofPictures?: string[]
    updatedBy: string | undefined
  }
  options?: UpsertProductOptions
}

export type CreateProductVars = BaseProductVariables & {
  upsertType: 'create' | 'batch'
}

type TrackProductVars = BaseProductVariables & {
  upsertType: 'update'
  /** Opt-in CAS. Legacy callers that omit this retain their existing path. */
  precondition?: ProductStatusUpdatePrecondition
}

type SplitProductVars = BaseProductVariables & {
  upsertType: 'split'
  splitedInput: Partial<Product> & {
    id: string
    progress: Progress
    type?: Status
    action: Action
    quantity?: Quantity
  }
}

export type UpsertProductVariables =
  | CreateProductVars
  | TrackProductVars
  | SplitProductVars

// Create, update and delete product status
const useProductCreateScopePreflight = () => {
  const prepareProductCreateAuthorizerScope =
    usePrepareProductCreateAuthorizerScope()

  return async (variables: UpsertProductVariables) => {
    if (variables.upsertType === 'create' || variables.upsertType === 'batch') {
      const organizationID = await prepareProductCreateAuthorizerScope({
        organizationID: variables.input.organizationID,
        organizationMasterID: variables.input.organizationMasterID,
      })
      if (!variables.input.organizationID && organizationID) {
        variables.input.organizationID = organizationID
      }
    }
  }
}

export const useUpsertProduct = () => {
  const queryClient = useQueryClient()
  const prepareProductCreateScope = useProductCreateScopePreflight()

  return useMutation({
    mutationKey: ['upsertProduct'],
    mutationFn: upsertProduct,
    onMutate: async (variables) => {
      // React Query runs onMutate before mutationFn. Keep the authorization
      // preflight ahead of both optimistic cache writes and GraphQL.
      await prepareProductCreateScope(variables)
      const { input } = variables

      // Determine if this operation moves products between different filtered views
      // (e.g., active -> done, done -> active, any -> deleted)
      // For restore operations, we need to check if we're moving FROM done TO active
      const isRestoreOperation =
        variables.upsertType === 'update' &&
        input.progress === 'active' &&
        input.action !== undefined // Restore operations set an action

      const isMovingBetweenViews =
        input.progress === 'done' ||
        input.type === 'deleted' ||
        variables.upsertType === 'split' ||
        isRestoreOperation

      if (isMovingBetweenViews) {
        // Use move-between-caches mode for operations that move products between views
        updateProductsCaches(
          {
            organizationID: input.organizationID,
            productStatusInput: input,
          },
          queryClient,
        )

        // Split need to update the previous status values (it's not done)
        if (variables.upsertType === 'split') {
          updateProductsCaches(
            {
              organizationID: input.organizationID,
              productStatusInput: variables.splitedInput,
            },
            queryClient,
          )
        }
      } else {
        // Use update-in-place mode for regular updates within the same view
        updateProductStatusCache(
          {
            organizationID: input.organizationID,
            productStatusInput: input,
          },
          queryClient,
          ['productStatuses'],
          'update-in-place',
        )
      }
    },
    onSuccess: (data, variables) => {
      const updatedAt =
        typeof data === 'string'
          ? data
          : data && 'updatedAt' in data
            ? data.updatedAt
            : undefined
      if (variables.upsertType !== 'update' || typeof updatedAt !== 'string') {
        return
      }
      const confirmedAt = Date.parse(updatedAt)
      if (!Number.isFinite(confirmedAt)) return

      // Reconcile only confirmed metadata. A newer edit may already have
      // changed the row, or its confirmation may have arrived first.
      queryClient.setQueriesData<ProductStatusesQueryData>(
        { queryKey: ['productStatuses'] },
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => {
                if (
                  item.id !== variables.input.id ||
                  Date.parse(item.updatedAt ?? '') > confirmedAt
                ) {
                  return item
                }
                return { ...item, updatedAt }
              }),
            })),
          },
      )
    },
    onSettled: (_data, _error, variables) => {
      if (variables?.options?.invalidate === false) return

      const productID = variables?.input?.productID
      if (productID) {
        queryClient.invalidateQueries({
          queryKey: ['product', productID],
        })
        queryClient.invalidateQueries({
          queryKey: ['changelogs', productID],
        })
        queryClient.invalidateQueries({
          queryKey: ['map'],
        })
      }
    },
  })
}

// Create, update and delete product status WITHOUT optimistic cache updates
// Use this hook when you want to preserve AG Grid's undo/redo stacks by avoiding
// immediate cache updates that would refresh rowData.
export const useUpsertProductNoOptimistic = () => {
  const queryClient = useQueryClient()
  const prepareProductCreateScope = useProductCreateScopePreflight()

  return useMutation({
    mutationKey: ['upsertProduct'],
    // A lost conditional response cannot safely be replayed.
    retry: false,
    mutationFn: async (variables: UpsertProductVariables) => {
      await prepareProductCreateScope(variables)
      return upsertProduct(variables)
    },
    // No onMutate - skip optimistic cache updates to preserve grid undo/redo stacks
    onError: async (_error, variables) => {
      variables?.options?.onError?.()

      if (variables?.options?.invalidate === false) return

      const productID = variables?.input?.productID
      if (!productID) return

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['product', productID],
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({
          queryKey: ['productStatuses'],
          refetchType: 'active',
        }),
      ])
    },
    onSuccess: (data, variables) => {
      const receipt =
        variables.upsertType === 'update' &&
        variables.precondition !== undefined
          ? (data as ProductStatusUpdateReceipt)
          : data && typeof data === 'object' && 'statusReceipt' in data
            ? data.statusReceipt
            : undefined
      variables?.options?.onSuccess?.(receipt)
    },
    onSettled: (_data, _error, variables) => {
      variables?.options?.onSettled?.()

      if (variables?.options?.invalidate === false) return

      const productID = variables?.input?.productID
      if (productID) {
        queryClient.invalidateQueries({
          queryKey: ['product', productID],
          refetchType: 'none',
        })
        queryClient.invalidateQueries({
          queryKey: ['changelogs', productID],
          refetchType: 'none',
        })
        queryClient.invalidateQueries({
          queryKey: ['map'],
          refetchType: 'none',
        })
        queryClient.invalidateQueries({
          queryKey: ['productStatuses'],
          refetchType: 'none',
        })
      }
    },
  })
}

// Relations should always be removed from input
const relationsFields = [
  'archived',
  'batchedProducts',
  'byUser',
  'createdAt',
  'draft',
  'place',
  'project',
  'shop',
  'updatedAt',
  'display_order',
  'refKey',
  'customID',
  'product',
  'header',
  'adapted',
  'key',
  'values',
  'reviews',
  'dimensions',
  'batchedProductIDs',
  'batched_quantity',
  'status',
]

const assertPersistableProductStatusInput = (
  input: Partial<Product> & { id?: string; productID?: string; slug?: string },
): void => {
  if (isTemporaryProductIdentity(input.id)) {
    throw new Error(
      `Cannot persist product status with temporary id ${input.id}`,
    )
  }

  if (isTemporaryProductIdentity(input.productID)) {
    throw new Error(
      `Cannot persist product status with temporary productID ${input.productID}`,
    )
  }

  if (hasTemporaryProductSlugIdentity(input.slug)) {
    throw new Error(
      `Cannot persist product status with temporary slug ${input.slug}`,
    )
  }
}

export const assertPersistableProductMutation = (
  variables: UpsertProductVariables,
): void => {
  assertPersistableProductStatusInput(variables.input)

  if (variables.upsertType === 'split') {
    assertPersistableProductStatusInput(variables.splitedInput)
  }
}

const upsertProduct = async (variables: UpsertProductVariables) => {
  const { upsertType } = variables
  const { input } = variables
  assertPersistableProductMutation(variables)

  switch (upsertType) {
    case 'create': {
      // The initial-status contract is the canonical create DTO. Do not run
      // it through the historical update relation cleanup: fields such as
      // draft are valid business fields in this contract.
      const productStatusInput = input as ProductStatusInput
      return createProductWithStatus({
        productID: input.productID,
        productName: input.name ?? 'Nom manquant',
        organizationID: input.organizationID,
        ...(input.nature !== undefined ? { productNature: input.nature } : {}),
        productStatusInput,
      })
    }
    case 'update': {
      const productStatusInput = cleanInput({
        input,
        keysToRemove: relationsFields,
      }) as ProductStatusInput
      return updateProductStatus({
        productStatusInput,
        precondition: variables.precondition,
      })
    }
    case 'split': {
      const productStatusInput = cleanInput({
        input,
        keysToRemove: relationsFields,
      }) as ProductStatusInput
      const previousStatusInput = cleanInput({
        input: variables.splitedInput,
        keysToRemove: relationsFields,
      }) as ProductStatusInput
      return closeAndCreateProductStatus({
        previousStatusInput: {
          ...previousStatusInput,
          progress: 'active',
          archived: false,
        },
        productStatusInput,
      })
    }
    case 'batch': {
      const productStatusInput = cleanInput({
        input,
        keysToRemove: relationsFields,
      }) as ProductStatusInput
      return createProductBatch({
        productBatchInput: {
          id: productStatusInput.id,
          productID: productStatusInput.productID,
          batchedStatusID: productStatusInput.id,
        },
        productStatusInput,
      })
    }
    default:
      console.info('upsertType not implemented for ', upsertType)
      return
  }
}

const createProductWithStatus = async ({
  productID,
  productName,
  productNature,
  organizationID,
  productStatusInput,
}: {
  productID: string
  productName: string
  productNature?: ProductStatusInput['nature']
  organizationID?: string
  productStatusInput: ProductStatusInput
}) => {
  if (!organizationID) {
    throw new Error('organizationID is required for product creation')
  }

  const input = buildCreateProductWithStatusInput({
    productID,
    productName,
    organizationID,
    productNature,
    productStatusInput,
  })

  try {
    const response = (await client.graphql({
      query: createProductWithStatusMutation,
      variables: { input },
    })) as GraphQLResult<ProductWithStatusCreation>
    const { data } = response
    const status = data?.createProductWithStatus.productStatus
    const statusReceipt: ProductStatusUpdateReceipt | undefined =
      !response.errors?.length &&
      status?.id === productStatusInput.id &&
      typeof status?.updatedAt === 'string' &&
      Number.isFinite(Date.parse(status.updatedAt))
        ? { id: status.id, updatedAt: status.updatedAt }
        : undefined
    return {
      product: data?.createProductWithStatus.product.createdAt,
      productStatus: status?.createdAt,
      ...(statusReceipt ? { statusReceipt } : {}),
    }
  } catch (e) {
    console.error('Error creating product with status', e)
    throw e
  }
}

const updateProductStatus = async (params: {
  productStatusInput: ProductStatusInput
  precondition?: ProductStatusUpdatePrecondition
}) => {
  const { productStatusInput, precondition } = params
  const expectedUpdatedAt = precondition?.expectedUpdatedAt
  if (
    precondition !== undefined &&
    (typeof expectedUpdatedAt !== 'string' ||
      !Number.isFinite(Date.parse(expectedUpdatedAt)))
  ) {
    const error = new Error('A confirmed product status version is required')
    error.name = 'ValidationError'
    throw error
  }
  try {
    const response = (await client.graphql({
      query: updateProductStatusMutation,
      variables: {
        input: {
          ...productStatusInput,
          ...(precondition !== undefined
            ? { expectedUpdatedAt }
            : { updatedAt: new Date().toISOString() }),
        },
      },
    })) as GraphQLResult<{
      updateProductStatus: ProductStatusUpdateReceipt | null
    }>
    if (precondition === undefined)
      return response.data?.updateProductStatus?.updatedAt
    if (response.errors?.length) throw response
    const receipt = response.data?.updateProductStatus
    if (
      receipt?.id !== productStatusInput.id ||
      typeof receipt?.updatedAt !== 'string' ||
      !Number.isFinite(Date.parse(receipt.updatedAt)) ||
      Date.parse(receipt.updatedAt) <= Date.parse(expectedUpdatedAt!)
    ) {
      const error = new Error(
        'Product status mutation receipt is missing or invalid',
      )
      error.name = 'ProductStatusOutcomeUnknown'
      throw error
    }
    return { id: receipt.id, updatedAt: receipt.updatedAt }
  } catch (e) {
    console.error('Error updating product status', e)
    throw e
  }
}

type TrackProductVariables = {
  previousStatusInput: Partial<Product> & {
    id: string
    progress: Progress
    archived?: boolean
  }
  productStatusInput: ProductStatusInput
}

const closeAndCreateProductStatus = async (params: TrackProductVariables) => {
  const { previousStatusInput, productStatusInput } = params
  try {
    const { data } = (await client.graphql({
      query: closeAndCreateProductStatusMutation,
      variables: {
        previousStatusInput: {
          ...previousStatusInput,
          updatedAt: new Date().toISOString(),
        },
        input: productStatusInput,
      },
    })) as GraphQLResult<ProductStatus>
    return data?.createProductStatus.createdAt
  } catch (e) {
    console.error('Error creating product status', e)
    throw e
  }
}

type ProductBatchParams = {
  productBatchInput: {
    id: string
    productID: string
    batchedStatusID: string
  }
  productStatusInput: ProductStatusInput
}
/** Create a ProductBatch, relation between a ProductStatus and Product
 *  Allows to make a batch (a group) of product statuses for a same product
// ProductStatus.batchedProductID === ProductBatch.id
// ProductBatch.batchedStatusID === ProductStatus.id
// ProductBatch.productID === Product.id
*/
const createProductBatch = async (params: ProductBatchParams) => {
  const { productBatchInput, productStatusInput } = params
  return await client.graphql({
    query: createProductBatchAndStatusMutation,
    variables: {
      productBatchInput,
      productStatusInput,
    },
  })
}

// Cache updates
export const updateProductStatusCache = async (
  variables: ProductStatusProps | ProductRestoration,
  queryClient: QueryClient,
  queryKey?: any,
  mode: 'update-in-place' | 'move-between-caches' = 'update-in-place',
) => {
  const { productStatusInput } = variables

  // Default to productStatuses if no queryKey is provided
  const effectiveQueryKey = queryKey || ['productStatuses']

  queryClient.cancelQueries({
    queryKey: effectiveQueryKey,
  })

  if (mode === 'move-between-caches') {
    // New behavior for ArchiveTrash
    updateProductsCaches(variables, queryClient)
  } else {
    // Original behavior for Grid
    const skippedQueryKeys: Array<readonly unknown[]> = []
    const previousProducts =
      queryClient.getQueriesData<ProductStatusesQueryData>({
        queryKey: effectiveQueryKey,
      })

    previousProducts?.forEach(([queryKey, data]) => {
      if (!data) {
        skippedQueryKeys.push(queryKey)
        return
      }

      const nextData = toProductStatusesQueryData({
        ...data,
        pages: replaceProductStatusInQueryData({
          previousProducts: data,
          variables: productStatusInput,
        }),
      })

      queryClient.setQueryData(queryKey, nextData)
    })

    skippedQueryKeys.forEach((queryKey) => {
      queryClient.invalidateQueries({
        queryKey,
        exact: true,
      })
    })
  }
}

// Update all products caches with different filter combinations
export const updateProductsCaches = async (
  variables: ProductStatusProps | ProductRestoration,
  queryClient: QueryClient,
) => {
  const { productStatusInput } = variables
  const { id } = productStatusInput

  // Get all productStatuses queries
  const allQueries = queryClient.getQueriesData<ProductStatusesQueryData>({
    queryKey: ['productStatuses'],
  })

  const oldProductQuery = allQueries.find(([_queryKey, data]) =>
    data?.pages?.some((page) => page.items?.some((item) => item.id === id)),
  )

  const oldProduct = oldProductQuery?.[1]?.pages
    .find((page) => page.items?.some((item) => item.id === id))
    ?.items?.find((item) => item.id === id)

  // Try to find a sibling product (same productID) to use as a template when the
  // exact id is not yet present in any cache (e.g., just-created split status).
  let templateProduct = oldProduct
  if (!templateProduct) {
    const siblingQuery = allQueries.find(([_queryKey, data]) =>
      data?.pages?.some((page) =>
        page.items?.some(
          (item) => item.productID === productStatusInput.productID,
        ),
      ),
    )
    templateProduct = siblingQuery?.[1]?.pages
      .find((page) =>
        page.items?.some(
          (item) => item.productID === productStatusInput.productID,
        ),
      )
      ?.items?.find((item) => item.productID === productStatusInput.productID)
  }

  // If we have a template, merge minimal server payload over it to keep a valid shape.
  // Otherwise we will not push and will rely on invalidation to refetch.
  const canPush = !!templateProduct
  const updatedProduct = templateProduct
    ? {
        ...templateProduct,
        ...productStatusInput,
        updatedAt: new Date().toISOString(),
      }
    : undefined

  let destinationCacheFound = false
  const skippedDestinationQueryKeys: Array<readonly unknown[]> = []

  allQueries.forEach(([queryKey, data]) => {
    if (!data) {
      if (matchesProductStatusesDestination(queryKey, productStatusInput)) {
        skippedDestinationQueryKeys.push(queryKey)
      }
      return
    }

    const currentData = toProductStatusesQueryData(data)

    // Remove outdated entries from all caches
    const pages = currentData.pages.map((page) => ({
      ...page,
      items: page.items?.filter((item) => item.id !== id),
    }))

    const isDestination = matchesProductStatusesDestination(
      queryKey,
      productStatusInput,
    )

    // Update the destination cache
    if (isDestination && canPush && updatedProduct) {
      destinationCacheFound = true
      if (!pages.length) {
        pages.push({ items: [] })
      }
      if (!pages[0].items) {
        pages[0].items = []
      }
      pages[0].items.unshift(updatedProduct)
    }

    queryClient.setQueryData(queryKey, {
      pages,
      pageParams: currentData.pageParams,
    })
  })

  // If no destination cache was found, invalidate queries to force refetch
  // This handles the case where the Grid hasn't been loaded yet
  if (!destinationCacheFound) {
    // Invalidate all productStatuses queries that match the destination criteria
    queryClient.invalidateQueries({
      queryKey: ['productStatuses'],
      predicate: (query) =>
        matchesProductStatusesDestination(query.queryKey, productStatusInput),
    })
    return
  }

  skippedDestinationQueryKeys.forEach((queryKey) => {
    queryClient.invalidateQueries({
      queryKey,
      exact: true,
    })
  })
}
